/**
 * Конфигурация виджета по умолчанию.
 * Все поля можно переопределить при инициализации MobileIDWidget.
 */
export const defaults = {

  // ─── SDK ───────────────────────────────────────────────────────────────────
  /** URL клиентского backend для получения init-токена. ОБЯЗАТЕЛЬНО. */
  tokenUrl: null,
  /** URL MobileID backend. Необязательно — SDK использует значение по умолчанию. */
  baseUrl: null,
  /** Код страны для нормализации номера. */
  country: 'RU',
  /** Номер телефона для авто-старта верификации при mount(). Экран ввода не показывается. */
  phone: null,

  // ─── Тексты ────────────────────────────────────────────────────────────────
  texts: {
    phoneLabel:       'Номер телефона',
    phonePlaceholder: '+7 (___) ___-__-__',
    submitPhone:      'Получить код',

    otpLabel:         'Введите код из SMS',
    otpPlaceholder:   '• • • •',
    submitOtp:        'Подтвердить',
    back:             'Изменить номер',

    pendingText:      'Отправляем запрос...',
    successTitle:     'Номер подтверждён',
    rejectedTitle:    'Верификация отклонена',
    retryBtn:         'Попробовать снова',
    rateLimitTitle:   'Превышен лимит запросов',

    errors: {
      invalidPhone:   'Введите корректный номер телефона',
      invalidCode:    'Неверный код. Попробуйте ещё раз',
      sessionExpired: 'Время сессии истекло. Попробуйте снова',
      networkError:   'Ошибка соединения. Попробуйте снова',
      unknown:        'Произошла ошибка. Попробуйте снова',
    },
  },

  // ─── Тема (цвета и геометрия) ──────────────────────────────────────────────
  theme: {
    primaryColor:     '#2563eb',  // основной цвет кнопки
    primaryHover:     '#1d4ed8',  // hover кнопки
    primaryText:      '#ffffff',  // цвет текста кнопки
    bgColor:          '#ffffff',  // фон виджета
    inputBg:          '#f9fafb',  // фон поля ввода
    inputBorder:      '#d1d5db',  // граница поля ввода
    inputBorderFocus: '#2563eb',  // граница поля ввода в фокусе
    inputText:        '#111827',  // цвет текста в поле ввода
    labelColor:       '#374151',  // цвет подписи
    hintColor:        '#6b7280',  // вспомогательный текст (кнопка «назад», спиннер)
    errorColor:       '#ef4444',  // цвет ошибок
    successColor:     '#16a34a',  // цвет успешного результата
    rejectedColor:    '#ef4444',  // цвет отклонения
    spinnerColor:     '#2563eb',  // цвет спиннера ожидания
    borderRadius:     '8px',      // скругление углов полей и кнопок
    fontFamily:       'inherit',  // шрифт
    fontSize:         '16px',     // базовый размер шрифта
    padding:          '12px',     // внутренние отступы полей и кнопок
    gapLabel:         '6px',      // отступ между label и input
    gap:              '10px',     // отступ между input/ошибкой и кнопкой
  },

  /**
   * Вид финального экрана (verified + rejected):
   *   'text'  — текстовое сообщение (по умолчанию)
   *   'phone' — задизейбленный input с номером телефона;
   *             зелёная обводка при успехе, красная при отклонении
   */
  resultView: 'text',

  // ─── Управление потоком ────────────────────────────────────────────────────
  /**
   * Разрешить пользователю вернуться к экрану ввода номера.
   * false — скрывает кнопки «Изменить номер» и «Попробовать снова».
   * Актуально когда номер задаётся извне (опция phone / startWithPhone).
   */
  allowChangePhone: true,

  // ─── Настройки поля ввода ──────────────────────────────────────────────────
  input: {
    /** Маска +7 (XXX) XXX-XX-XX для поля телефона */
    phoneMask:    true,
    /** Включить autocomplete="tel" / "one-time-code" */
    autocomplete: true,
    /** Длина OTP-кода (используется для maxlength и авто-сабмита) */
    otpLength:      4,
    /** Тип <input> для OTP: 'tel' | 'number' | 'text' */
    otpType:        'tel',
    /**
     * Автоматически отправлять код при вводе последней цифры.
     * true  — кнопка «Подтвердить» не показывается.
     * false — показывается кнопка, отправка только по клику / Enter.
     */
    autoSubmitOtp:  false,
  },

  // ─── Настройки кнопки ──────────────────────────────────────────────────────
  button: {
    /** Кнопка на всю ширину контейнера */
    fullWidth:    true,
    /** Скругление углов. null = наследует theme.borderRadius */
    borderRadius: null,
  },

  // ─── Кастомные стили ───────────────────────────────────────────────────────
  /** Строка CSS, которая инжектируется в Shadow DOM поверх базовых стилей виджета */
  customCss:    null,
  /** URL CSS-файла. Загружается fetch'ем и инжектируется в Shadow DOM */
  customCssUrl: null,

  // ─── Капча ─────────────────────────────────────────────────────────────────
  /**
   * Async-функция, которая должна вернуть токен капчи перед отправкой номера.
   * Кнопка блокируется на время ожидания; экран не меняется.
   * Токен передаётся бэкенду как query-параметр captcha_token в tokenUrl.
   *
   * Работает с любым провайдером: invisible (reCAPTCHA v3, Turnstile)
   * и интерактивным (reCAPTCHA v2, hCaptcha).
   *
   * @example
   * // reCAPTCHA v3
   * getCaptchaToken: () => grecaptcha.execute('SITE_KEY', { action: 'mobileid' })
   *
   * @example
   * // hCaptcha / reCAPTCHA v2 (интерактив — промис резолвится после прохождения)
   * getCaptchaToken: () => new Promise((resolve) => {
   *   showCaptcha({ onSolved: resolve });
   * })
   */
  getCaptchaToken: null,

  // ─── Коллбэки ──────────────────────────────────────────────────────────────
  /** Верификация пройдена. Аргумент: { verify_token, phone, session_id } */
  onVerified: null,
  /** Верификация отклонена. Аргумент: { verify_token, session_id } */
  onRejected: null,
  /** Произошла ошибка. Аргумент: { code, message } */
  onError:      null,
  /** Превышен лимит запросов. Аргументов нет */
  onRateLimit:  null,
  /** Сессия создана, виджет готов к вводу номера */
  onReady:      null,

};
